import React from "react";
import { 
  Stack, 
  Button, 
  Box, 
  Typography,
} from "@mui/material";
import { RotateCcw, Filter } from "lucide-react";
import Select from "react-select";

const PRIMARY_COLOR = "#bb1ccc";
const PRIMARY_DARK = "#000080";

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#F4F6F8",
    borderColor: state.isFocused ? PRIMARY_COLOR : "#E5E7EB",
    borderWidth: state.isFocused ? 2 : 1,
    borderRadius: 8,
    minHeight: 45,
    boxShadow: state.isFocused ? `0 0 0 1px ${PRIMARY_COLOR}` : "none",
    "&:hover": {
      borderColor: state.isFocused ? PRIMARY_COLOR : "#CBD5F5",
    },
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "rgba(0, 0, 160, 0.1)",
    borderRadius: 4,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: PRIMARY_COLOR,
    fontSize: 12,
    fontWeight: 500,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: PRIMARY_COLOR,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 160, 0.2)",
      color: PRIMARY_COLOR,
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9CA3AF",
    fontSize: 14,
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: 8,
    boxShadow: "0 4px 14px rgba(15,23,42,0.1)",
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? "rgba(0, 0, 160, 0.1)" 
      : state.isFocused 
        ? "rgba(0, 0, 160, 0.05)" 
        : "white",
    color: state.isSelected ? PRIMARY_COLOR : "#374151",
    fontSize: 14,
    "&:active": {
      backgroundColor: "rgba(0, 0, 160, 0.15)",
    },
  }),
};

const FeatureFilters = ({
  statusFilter = [],  
  categoryFilter = [], 
  searchQuery,
  total,
  onStatusChange,
  onCategoryChange,
  onSearchChange,
  statusOptions,
  categoryOptions,
  filtersVisible = false,
  onToggleFilters = () => { },
  onResetFilters = () => { },
  onApplyFilters = () => { },
  rightActions = null,
}) => {
  const statusArray = Array.isArray(statusFilter) ? statusFilter : [];
  const categoryArray = Array.isArray(categoryFilter) ? categoryFilter : [];

  const disableApply = false

  const fieldWrapperSx = {
    flex: 1,
    minWidth: 200,
  };

  const statusSelectOptions = (statusOptions?.filter(opt => opt.value !== "ALL") || [])
    .map(opt => ({ value: opt.value, label: opt.label }));
  const categorySelectOptions = (categoryOptions?.filter(opt => opt.value !== "ALL") || [])
    .map(opt => ({ value: opt.value, label: opt.label }));

  const selectedStatusOptions = statusSelectOptions.filter(opt => statusArray.includes(opt.value));
  const selectedCategoryOptions = categorySelectOptions.filter(opt => categoryArray.includes(opt.value));

  const handleStatusChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    onStatusChange(values);
  };

  const handleCategoryChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    onCategoryChange(values);
  };

  return (
    <Stack spacing={2.5} mb={3}>
      <div className="flex justify-between"
      >
        <div className="bg-white p-2">
          <div className="flex items-center justify-between ">
            <div className="flex items-center gap-2">
              <button
                onClick={onToggleFilters}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap ${filtersVisible
                  ? 'bg-[#bb1ccc] text-white border-[#bb1ccc]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <Filter className="h-4 w-4" />
                {filtersVisible ? 'Hide Filters' : 'Show Filters'}
              </button>
              <button
                onClick={onResetFilters}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-g rounded-lg hover:bg-[#bb1ccc] flex items-center gap-2 h-[45px] whitespace-nowrap"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
            </div>

            <div className="flex-1"></div>
          </div>


        </div>
        {rightActions && (
          <Stack direction="row" spacing={1}>
            <Stack spacing={0.5} sx={fieldWrapperSx}>
            </Stack>
            {rightActions}
          </Stack>
        )}
      </div>


      {filtersVisible && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            backgroundColor: "#fff",
            boxShadow: "0 4px 14px rgba(15,23,42,0.05)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2.5}
            alignItems={{ xs: "stretch", md: "flex-end" }}
          >
            <Stack spacing={0.5} sx={fieldWrapperSx}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "#1f2937", fontSize: "14px" }}
              >
                Status
              </Typography>
              <Select
                isMulti
                value={selectedStatusOptions}
                onChange={handleStatusChange}
                options={statusSelectOptions}
                placeholder="Select Status"
                styles={customSelectStyles}
                isClearable
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                noOptionsMessage={() => "No status found"}
              />
            </Stack>

            <Stack spacing={0.5} sx={fieldWrapperSx}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "#1f2937", fontSize: "14px" }}
              >
                Category
              </Typography>
              <Select
                isMulti
                value={selectedCategoryOptions}
                onChange={handleCategoryChange}
                options={categorySelectOptions}
                placeholder="Select Category"
                styles={customSelectStyles}
                isClearable
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                noOptionsMessage={() => "No category found"}
              />
            </Stack>



            <Button
              variant="contained"
              onClick={onApplyFilters}
              disabled={disableApply}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                px: 4,
                height: 45,
                bgcolor: disableApply ? "rgba(0,0,160,0.2)" : PRIMARY_COLOR,
                color: disableApply ? "rgba(15,23,42,0.5)" : "#fff",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: disableApply ? "rgba(0,0,160,0.2)" : PRIMARY_DARK,
                },
              }}
            >
              Apply Filters
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default FeatureFilters;



